import Options from "./components/Options.vue";
import Listing from "./components/Listing.vue";
import MLSList from "./components/MLSList.vue";
import Report from "./components/Report.vue";
import planningReport from "./components/planningReport.vue";
import Projects from "./components/NewProjects.vue";
import UnitMix from "./components/UnitMix.vue";
import UTC from "./components/UTC.vue";
import PasswordReset from "./components/PasswordReset.vue";
import OnetimeLogin from "./components/OnetimeLogin.vue";
import Contact from "./components/Contact.vue";
import MLSEntry from "./components/MLSEntry.vue";
import MLSEntryEdit from "./components/MLSEntryEdit.vue";
import LandList from "./components/LandList.vue";
import LandListNew from "./components/LandListNew.vue";
import Plans from "./components/Plans.vue";
import Permits from "./components/Permits.vue";
import UnitReport from "./components/UnitReport.vue";
import VueRouter from "vue-router";
import { canUseChatBot, canUseTasks, isSuperAdmin } from "./variables.js";
import { useUserStore } from "./store/UserStore";
import { useUserDataStore } from "./store/UserDataStore";
import { useTaskStore } from "./store/TaskStore";
import { async } from "regenerator-runtime";

function loadComponent(component) {
  return import(`./components/${component}.vue`);
}
function loadView(component) {
  return import(`./views/${component}.vue`);
}

function loadDashboardComponent(component) {
  return import(`./components/dashboard/${component}.vue`);
}

function loadTaskComponent(component) {
  return import(`./components/tasks/${component}.vue`);
}
const authenticateUser = async (to, from, next) => {
  let res = await useUserStore().checkLogin();
  // One list for every guard: isSuperAdmin() in variables.js (backend flag,
  // email fallback).
  const superAdmin = isSuperAdmin(useUserStore().getUser())
  const isAdmin = useUserStore().getUser().role === 'admin';
  if (res) {
    if (to.name == 'approvalmanagement' && superAdmin)
      next();

    if (to.name == 'submissions' && isAdmin)
      next();

    if (to.name == 'approvalmanagement' && !superAdmin)
      router.push({ name: "restricted" })

    else next()
    return;
  } else router.push({ name: "restricted" }).catch(() => { });
};

// Chat bot routes. Open to every admin role, tested through canUseChatBot so
// this guard and the launcher read the same rule — see variables.js, and
// presaledb_chatbot_allowed() for the server-side copy.
//
// Deliberately its own guard rather than another named-route branch inside
// authenticateUser: that function's if-chain falls through to its trailing
// `else next()` after matching a case, so a route added there can end up calling
// next() twice. This one has a single exit per outcome.
const authenticateChatBot = async (to, from, next) => {
  const loggedIn = await useUserStore().checkLogin();
  if (loggedIn && canUseChatBot(useUserStore().getUser())) {
    next();
    return;
  }
  router.push({ name: "restricted" }).catch(() => { });
};

// Admin-only routes (user administration). Same reasoning as
// authenticateChatBot above: a dedicated guard rather than another named-route
// branch inside authenticateUser, so there is exactly one exit per outcome and
// no fall-through to that function's trailing `else next()`.
const isAdminUser = (user) => !!user && user.role === "admin";

const authenticateAdmin = async (to, from, next) => {
  const loggedIn = await useUserStore().checkLogin();
  if (loggedIn && isAdminUser(useUserStore().getUser())) {
    next();
    return;
  }
  router.push({ name: "restricted" }).catch(() => { });
};

// Super-admin-only routes (changing user access rules). Its own guard, for the
// same reason as authenticateAdmin above: one exit per outcome. The backend
// checks the same list again on every write (rpd_is_super_admin()).
const authenticateSuperAdmin = async (to, from, next) => {
  const loggedIn = await useUserStore().checkLogin();
  const user = useUserStore().getUser();
  if (loggedIn && isAdminUser(user) && isSuperAdmin(user)) {
    next();
    return;
  }
  router.push({ name: "restricted" }).catch(() => { });
};

// RPD Tasks routes. Its own guard, for the same reason as authenticateChatBot
// and authenticateAdmin above: one exit per outcome, and no fall-through to
// authenticateUser's trailing `else next()`.
//
// The test is only "does the backend resolve this account to a task level" —
// it deliberately does not reimplement the permission matrix. What the user
// may *do* comes from api/tasks/me, and per card from api/tasks/gates, so
// there is exactly one copy of the rules and it lives in
// inc/rpd_tasks.access.inc.

// The routes that make up the worklist. Moving between them is a drawer
// interaction, not a fresh entry into the section.
const TASK_ROUTES = ['tasks', 'task', 'taskperson'];

const authenticateTasks = async (to, from, next) => {
  // Already inside the section: the guard passed on the way in, so re-running
  // it would fire api/current_user, api/get_role and api/tasks/me on every
  // single card click. This gates entry only — it is not authorization. Every
  // endpoint still checks rpd_tasks_can() per request, so skipping here cannot
  // widen what the account may do.
  if (TASK_ROUTES.includes(from.name)) {
    next();
    return;
  }
  const loggedIn = await useUserStore().checkLogin();
  if (!loggedIn) {
    router.push({ name: "restricted" }).catch(() => { });
    return;
  }
  const session = await useTaskStore().loadSession();
  if (canUseTasks(session)) {
    next();
    return;
  }
  router.push({ name: "restricted" }).catch(() => { });
};

// Public-with-LinkedIn routes (/quarterly-trending-analysis). An RPD account
// or a bare LinkedIn sign-in with no RPD role (UserStore.viewerOnly) both get
// in; the page itself reads viewerOnly to decide what is clickable. Anyone
// else goes to the LinkedIn sign-in page, which sends them back here after.
const authenticateLinkedIn = async (to, from, next) => {
  const store = useUserStore();
  const hasAccount = await store.checkLogin();
  if (hasAccount || store.viewerOnly) {
    next();
    return;
  }
  next({ path: "/presale-database", query: { return: to.path.replace(/^\//, "") } });
};

const checkUser = async (to, from, next) => {
  await useUserStore().checkLogin();
  // window.innerWidth <= 576 ? next("/landing") : next();
  next();
};

const routes = [
  {
    name: "index",
    path: "/",
    component: () => loadComponent("Index"),
    beforeEnter: checkUser,
  },
  {
    name: "listsearch",
    path: "/list-search",
    component: () => loadView("ListSearch"),
    beforeEnter: checkUser,
  },
  {
    name: "approvalmanagement",
    path: "/approval-management",
    components: {
      main: () => loadDashboardComponent("ApprovalManagement"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateUser,
  },
  {
    name: "submissions",
    path: "/submissions",
    components: {
      main: () => loadDashboardComponent("Submissions"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateUser,
  },
  {
    name: "presale",
    path: "/presale-page",
    components: {
      main: () => loadDashboardComponent("Presale"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateUser,
  },
  {
    name: "login",
    path: "/presale-database",
    component: () => loadComponent("Login"),
    beforeEnter: async (to, from, next) => {
      const loggedIn = await useUserStore().checkLogin();
      // Already signed in with LinkedIn but no RPD role: LinkedIn refuses a
      // second sign-in, so go straight back to the page that sent them.
      if (!loggedIn && useUserStore().viewerOnly && to.query.return) {
        next('/' + to.query.return);
        return;
      }
      loggedIn ? next('/') : next();
    },
  },
  {
    name: "stackingplan",
    path: "/stacking-plan/:urlParams",
    component: () => loadComponent("StackingPlan"),
  },
  {
    name: "login",
    path: "/custom-login",
    component: () => loadComponent("CustomLogin"),
  },
  {
    name: "options",
    path: "/options",
    component: () => loadComponent("Options"),
  },
  { name: "report", path: "/report", component: () => loadComponent("Report") },
  { name: "market-search", path: "/market-search", component: () => loadComponent("MarketSearch"), beforeEnter: authenticateUser },
  {
    // Full-page view of a chat bot result set. The filters ride on
    // ?answers=<json>, so the widget can open this in a new tab and the link
    // stays shareable — see views/ChatBotResults.vue.
    name: "chatbot-results",
    path: "/chatbot-results",
    component: () => loadView("ChatBotResults"),
    beforeEnter: authenticateChatBot,
  },
  {
    name: "contact",
    path: "/contact",
    component: () => loadComponent("Contact"),
  },
  {
    name: "restricted",
    path: "/restricted",
    component: () => loadComponent("Restricted"),
  },
  {
    name: "user-management",
    path: "/user-management",
    component: () => loadComponent("UserManagement"),
    beforeEnter: authenticateAdmin,
  },
  {
    name: "account-overview",
    path: "/account-overview",
    component: () => loadComponent("AccountOverview"),
    beforeEnter: authenticateAdmin,
  },
  // Which cities and unit types one non-admin user may see (?user=<uid>).
  {
    name: "user-access",
    path: "/user-access",
    component: () => loadComponent("UserAccess"),
    beforeEnter: authenticateSuperAdmin,
  },
  // RPD Tasks. /tasks is the board; /tasks/:cardRef opens one card in the
  // drawer, so a card is linkable and the back button closes it.
  {
    name: "tasks",
    path: "/tasks",
    components: {
      main: () => loadTaskComponent("TaskBoard"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateTasks,
  },
  {
    name: "task",
    path: "/tasks/:cardRef",
    components: {
      main: () => loadTaskComponent("TaskBoard"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateTasks,
  },
  {
    name: "taskperson",
    path: "/people/:uid",
    components: {
      main: () => loadTaskComponent("PersonProfile"),
      sidebar: () => loadDashboardComponent("LeftSidebar"),
    },
    beforeEnter: authenticateTasks,
  },
  {
    path: "*",
    redirect: "/restricted",
  },
  {
    path: "/saved-properties",
    component: () => loadComponent("SavedProperties"),
  },
  { path: "/landing", component: () => loadComponent("LandingPage") },
  { path: "/demo", component: () => loadComponent("BookingCalendar") },
  {
    path: "/subscription-agreement",
    component: () => loadComponent("SubscriptionAgreement"),
    beforeEnter: authenticateUser,
  },
  {
    path: "/listing-new",
    component: () => import("./layouts/ListingLayout.vue"),
    beforeEnter: authenticateUser,
    children: [
      {
        path: "",
        component: () => import("./views/MarketStrata.vue"),
      },
    ],
  },
  {
    path: "/market-overview",
    component: () => import("./layouts/ListingLayout.vue"),
    beforeEnter: authenticateUser,
    children: [
      {
        path: "",
        name: "market-overview",
        component: () => import("./views/MarketOverview.vue"),
      },
    ],
  },
  {
    path: "/planning",
    component: () => import("./layouts/ListingLayout.vue"),
    beforeEnter: authenticateUser,
    children: [
      {
        path: "",
        name: "planning",
        component: () => import("./views/Planning.vue"),
      },
    ],
  },
  {
    path: "/land",
    component: () => import("./layouts/ListingLayout.vue"),
    beforeEnter: authenticateUser,
    children: [
      {
        path: "",
        name: "land",
        component: () => loadView("Land"),
      },
    ],
  },
  { path: "/utc-report-planning", component: planningReport },
  { path: "/listing", component: Listing },
  {
    path: "/unit-mix",
    component: () => loadComponent("UnitMix"),
    beforeEnter: authenticateUser,
  },
  // The investor rental report is now the Investor Rental tab of the MLS unit
  // report (see MLSList.vue). The old path is kept and redirects onto that tab
  // so existing links and bookmarks still land on the same report.
  {
    name: "rental-report",
    path: "/rental-report",
    redirect: (to) => ({
      path: "/mls-list",
      query: { ...to.query, rpt: "investor" },
    }),
  },
  {
    name: "unit-report",
    path: "/unit-report",
    component: UnitReport,
    beforeEnter: authenticateUser,
  },
  {
    name: "unit-mix-report",
    path: "/unit-mix-report",
    component: () => loadComponent("UnitMixReport"),
    beforeEnter: authenticateUser,
  },
  {
    path: "/mls-list",
    component: () => loadComponent("MLSList"),
    beforeEnter: authenticateUser,
  },
  { path: "/land-listings", name: "LandListings", component: LandList, beforeEnter: authenticateUser },
  { path: "/land-list-new", component: LandListNew, beforeEnter: authenticateUser },
  { path: "/project", component: Projects, beforeEnter: authenticateUser },
  {
    path: "/type",
    name: "utc",
    component: () => {
      return loadComponent("ProjectDetail");
    },
    beforeEnter: authenticateUser,
  },
  {
    name: "utc-legacy",
    path: "/type-old",
    component: UTC,
    beforeEnter: authenticateUser,
  },
  { path: "/100millionclub", component: () => loadComponent("100MillionClub") },
  {
    name: "developer-results",
    path: "/developer-results",
    component: () => loadComponent("DeveloperResults"),
    beforeEnter: authenticateUser,
  },
  // The developer index — every developer on a live project, with its project
  // count, unit total and pipeline split. Its own page rather than a popup on
  // Market Overview: it is read at the length of a list (searched, re-sorted,
  // filtered, paged) and its rows open /developer-results above, so it needs to
  // stay put while a reader works through it. Not admin-gated — the grid's
  // developer column and the per-developer report are open to every role.
  {
    name: "developer-search",
    path: "/developer-search",
    component: () => loadView("DeveloperSearch"),
    beforeEnter: authenticateUser,
  },
  {
    name: "terms",
    path: "/terms",
    component: () => loadComponent("Terms"),
  },
  {
    name: "unitsummary",
    path: "/unit-summary",
    component: () => loadComponent("UnitSummary"),
  },
  {
    name: "soldquarter",
    path: "/sold-quarter",
    component: () => loadComponent("SoldQuarter"),
  },
  // Quarterly Trending Analysis. Reads api/market-charts/*
  // (quarterly_trending_api.inc), which has its own absorption and sale-record
  // implementations.
  //
  // PUBLIC TO ANYONE SIGNED IN WITH LINKEDIN (authenticateLinkedIn), RPD account
  // or not. Without an RPD role the charts draw but nothing opens the records
  // behind a mark; api/market-charts/records refuses them server-side too.
  //
  // Open to every RPD role. It was admin-only for one release while the
  // sub-region tiers went out; it is not gated now. Nothing on the way in needs
  // unlocking either — every api/market-charts/* route already answers on the
  // caller's own dataset role (presaledb_mo_dataset_role) rather than refusing
  // a non-admin. The page keeps ONE tier of its own: the completion chart's
  // "From" control reaches back to Q1-2022 for an admin and Q1-2024 for
  // everyone else.
  {
    name: "quarterly-trending-analysis",
    path: "/quarterly-trending-analysis",
    component: () => loadComponent("QuarterlyTrendingAnalysis"),
    beforeEnter: authenticateLinkedIn,
  },
  // Absorptions and Unit Report over one Market Overview selection, a tab each.
  {
    name: "project-reports",
    path: "/project-reports",
    component: () => loadView("ProjectReports"),
    beforeEnter: authenticateUser,
  },
  {
    name: "absorptions",
    path: "/absorptions",
    component: () => loadComponent("Absorptions"),
    beforeEnter: authenticateUser,
  },
  {
    name: "submission",
    path: "/submission",
    component: () => loadComponent("Submission"),
  },
  { path: "/reset-password", component: PasswordReset },
  { path: "/onetime-login", component: OnetimeLogin },
  { path: "/mls_entry_update", component: MLSEntry },
  { path: "/mls_entry_edit", name: "MlsEntryEdit", component: MLSEntryEdit },
  { path: "/plans", component: Plans },
  { path: "/planning-updates", component: Permits },
  {
    path: "/project-detail",
    name: "project-detail",
    component: () => loadComponent("ProjectDetail"),
    beforeEnter: authenticateUser,
  },
];

const router = new VueRouter({
  routes,
  mode: "history",
});
router.afterEach((to, from) => {
  if (window.clicky) {
    if (to.path == '/type') {
      const currentPath = to.fullPath;
      window.clicky.log(currentPath, document.title, 'pageview');
    }
  }
});

router.afterEach((to) => {
  const logToClicky = () => {
    if (window.clicky) {
      let trackingUrl = to.fullPath;

      if (to.path === '/type') {
        window.clicky.log(trackingUrl, document.title, 'pageview');
      }
    }
  };

  if (!window.clicky) {
    const interval = setInterval(() => {
      if (window.clicky) {
        logToClicky();
        clearInterval(interval);
      }
    }, 100);
    setTimeout(() => clearInterval(interval), 5000);
  } else {
    logToClicky();
  }
});
export default router;
